/**
 * AdminParser — dedicated admin section for Original-Files parser:
 *   1. Full-width stats card (total, added this week, added this month,
 *      total brands).
 *   2. Parser config (URL / username / password / verify_ssl) + "Save".
 *   3. "Sync now" — pulls the upstream xlsx in the background and polls
 *      until done.
 *   4. "Upload xlsx file" — admin can drop a fresh xlsx and merge it
 *      into the catalog without going through the parser endpoint.
 *   5. Last-sync status banner.
 *
 * This component intentionally lives outside the generic Original Files
 * settings page so the admin can find data-source operations in one
 * place without scrolling past the customer-copy editors.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  RefreshCw, Save, Loader2, AlertCircle, CheckCircle2, Link2, Upload,
  Database, TrendingUp, Calendar, Tag, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminParser({ token }) {
  const [cfg, setCfg] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingCfg, setSavingCfg] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [testing,   setTesting]   = useState(false);
  const [testReport, setTestReport] = useState(null);
  const fileInputRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [cR, sR] = await Promise.all([
        fetch(`${API_URL}/api/original-files/admin/parser-config`, { headers }),
        fetch(`${API_URL}/api/original-files/stats`, { headers }),
      ]);
      if (cR.ok) setCfg(await cR.json());
      if (sR.ok) setStats(await sR.json());
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  const upd = (k, v) => setCfg((p) => ({ ...p, [k]: v }));

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      const body = {
        url: cfg.url, username: cfg.username,
        verify_ssl: !!cfg.verify_ssl,
        schedule_enabled:           !!cfg.schedule_enabled,
        schedule_interval_minutes:  Number(cfg.schedule_interval_minutes) || 1440,
      };
      // Only forward the password if the admin actually edited it.
      if (cfg.password && cfg.password !== '********') body.password = cfg.password;
      const r = await fetch(`${API_URL}/api/original-files/admin/parser-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setCfg(await r.json());
      toast.success('Parser config saved');
    } catch (e) {
      toast.error('Save failed', { description: e.message });
    } finally { setSavingCfg(false); }
  };

  // Save schedule alone (used by the toggle and dropdown for instant
  // feedback without forcing the admin to also click "Save config").
  const saveSchedule = async (next) => {
    try {
      const r = await fetch(`${API_URL}/api/original-files/admin/parser-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify(next),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d?.detail || `HTTP ${r.status}`);
      }
      setCfg(await r.json());
    } catch (e) {
      toast.error('Schedule update failed', { description: e.message });
      // Revert the optimistic UI on failure.
      refresh();
    }
  };

  // Polls /admin/parser-config until last_sync_state flips back to idle.
  // Returns the final config doc (which contains counts/error) or null
  // on timeout. Used by both runSync (HTTP-pulled xlsx) and runUpload.
  const waitForCompletion = async () => {
    const MAX_ATTEMPTS = 90;
    for (let i = 0; i < MAX_ATTEMPTS; i++) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((res) => setTimeout(res, 4000));
      // eslint-disable-next-line no-await-in-loop
      const r = await fetch(`${API_URL}/api/original-files/admin/parser-config`, { headers });
      if (!r.ok) continue;
      // eslint-disable-next-line no-await-in-loop
      const d = await r.json();
      if (d?.last_sync_state === 'idle') return d;
    }
    return null;
  };

  const finishWith = (d, mode = 'full') => {
    setCfg(d);
    if (d.last_sync_error) {
      toast.error('Sync failed', { description: d.last_sync_error });
    } else if (mode === 'delta' && (d.last_sync_total_rows ?? 0) === 0) {
      // Delta only returns rows the upstream parser hasn't flagged as
      // delivered yet. Rows it already marked exported can NEVER come back
      // through delta — if the catalog is behind, only a full sync fixes it.
      toast.warning('Delta returned 0 rows — nothing new upstream', {
        description: 'If Total files is still behind the parser\'s own count, run "Sync full" — delta cannot re-deliver rows the parser already marked as exported.',
        duration: 12000,
      });
    } else {
      toast.success(
        `Synced ${d.last_sync_total_rows ?? 0} rows`,
        { description: `Inserted ${d.last_sync_inserted} · Updated ${d.last_sync_updated} · Skipped ${d.last_sync_skipped} · ${Math.round(d.last_sync_elapsed_sec || 0)}s` },
      );
    }
    refresh();
  };

  const runTest = async (mode = 'full') => {
    if (testing || syncing || uploading) return;
    setTesting(true);
    setTestReport(null);
    try {
      const r = await fetch(`${API_URL}/api/original-files/admin/test-parser?mode=${mode}`, {
        method: 'POST', headers,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `HTTP ${r.status}`);
      setTestReport(data);
      // Toast a one-line summary so the admin sees intent-vs-result at a glance.
      if (data.error) {
        toast.error('Parser test failed', { description: data.error });
      } else if (data.rows_with_brand_model === 0 && data.total_rows_in_file > 0) {
        toast.warning('Parser export has rows but our importer would skip them all', {
          description: 'Check "Columns ignored" below — the header names likely changed.',
        });
      } else if (data.rows_with_brand_model === 0) {
        toast.warning('Parser export is empty', {
          description: `Downloaded ${data.downloaded_bytes} bytes, ${data.total_rows_in_file} raw rows. Check parser URL / mode.`,
        });
      } else {
        toast.success(`Parser test OK — ${data.rows_with_brand_model} rows would be imported`);
      }
    } catch (err) {
      toast.error('Test failed', { description: err.message });
    } finally { setTesting(false); }
  };

  const runSync = async (mode = 'full') => {
    if (syncing || uploading) return;
    setSyncing(true);
    toast.info(
      mode === 'delta' ? 'Delta sync started' : 'Full sync started',
      { description: mode === 'delta'
          ? 'Pulling only new rows since last delta — usually completes in <30s.'
          : 'Pulling full catalog from parser… can take 2–3 min.' },
    );
    try {
      const r = await fetch(`${API_URL}/api/original-files/admin/sync-from-parser?mode=${mode}`, {
        method: 'POST', headers,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.detail || `HTTP ${r.status}`);
      const final = await waitForCompletion();
      if (!final) throw new Error('Timed out waiting for sync to finish (>6 min). Check backend logs.');
      finishWith(final, mode);
    } catch (e) {
      toast.error('Sync failed', { description: e.message });
      refresh();
    } finally { setSyncing(false); }
  };

  const runUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (uploading || syncing) return;
    setUploading(true);
    toast.info('Importing xlsx', { description: `${f.name} — parsing & upserting…` });
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await fetch(`${API_URL}/api/original-files/admin/import-xlsx`, {
        method: 'POST', headers, body: fd,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.detail || `HTTP ${r.status}`);
      toast.success(
        `Imported ${data.total_rows ?? 0} rows`,
        { description: `Inserted ${data.inserted} · Updated ${data.updated} · Skipped ${data.skipped} · ${Math.round(data.elapsed_sec || 0)}s` },
      );
      refresh();
    } catch (err) {
      toast.error('Import failed', { description: err.message });
      refresh();
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading parser config…
      </div>
    );
  }
  if (!cfg) return <div className="p-8 text-center text-rose-600">Failed to load parser config.</div>;

  const lastErr = cfg.last_sync_error;
  const lastAt  = cfg.last_sync_at;
  const busy    = syncing || uploading;

  return (
    <div className="space-y-5" data-testid="admin-parser-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <Database className="w-5 h-5 text-cyan-600" /> Parser & Catalog Sync
          <span className="text-[11px] font-normal text-slate-500">Pulls or uploads the Original-Files xlsx into the catalog.</span>
        </h2>
      </header>

      {/* ── Catalog stats ────────────────────────────────── */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-parser-stats">
        <StatTile icon={Database}    color="cyan"    label="Total files"        value={stats?.total} />
        <StatTile icon={Calendar}    color="emerald" label="Added this month"   value={stats?.added_last_month} />
        <StatTile icon={TrendingUp}  color="violet"  label="Added this week"    value={stats?.added_last_week} />
        <StatTile icon={Tag}         color="amber"   label="Brands"             value={stats?.total_brands} />
      </section>

      {/* ── Parser config ────────────────────────────────── */}
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 sm:p-5">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-3">
          <Link2 className="w-4 h-4 text-cyan-600" /> Parser source
          <span className="text-[11px] font-normal text-slate-500">URL + Basic-auth credentials of the upstream catalog.</span>
        </h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2.5 mb-3">
          <label className="block sm:col-span-2 lg:col-span-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Parser URL</span>
            <input
              type="text" value={cfg.url || ''} onChange={(e) => upd('url', e.target.value)}
              placeholder="https://example/export.xlsx?mode=full"
              className="mt-1 w-full border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm font-mono"
              data-testid="admin-parser-url"
            />
          </label>
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Username</span>
            <input
              type="text" value={cfg.username || ''} onChange={(e) => upd('username', e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm font-mono"
              data-testid="admin-parser-user"
            />
          </label>
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Password</span>
            <input
              type="password" value={cfg.password || ''} onChange={(e) => upd('password', e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm font-mono"
              data-testid="admin-parser-pass"
            />
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox" checked={!!cfg.verify_ssl}
              onChange={(e) => upd('verify_ssl', e.target.checked)}
              data-testid="admin-parser-verify"
            />
            Verify SSL certificate
          </label>
          <button
            onClick={saveCfg} disabled={savingCfg}
            className="px-3 py-1.5 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
            data-testid="admin-parser-save"
          >
            {savingCfg ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save config
          </button>
        </div>
      </section>

      {/* ── Auto-sync schedule ───────────────────────────── */}
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 sm:p-5" data-testid="admin-parser-schedule">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-cyan-600" /> Auto-sync schedule
          <span className="text-[11px] font-normal text-slate-500">
            Re-pulls the catalog automatically. Off by default — click Sync now manually if you prefer.
          </span>
        </h3>
        <div className="flex flex-wrap items-center gap-4">
          {/* Enable toggle */}
          <label className="inline-flex items-center gap-2 select-none cursor-pointer" data-testid="admin-parser-schedule-enabled-label">
            <span className="relative">
              <input
                type="checkbox"
                checked={!!cfg.schedule_enabled}
                onChange={(e) => {
                  const next = { ...cfg, schedule_enabled: e.target.checked };
                  setCfg(next);
                  saveSchedule({ schedule_enabled: e.target.checked });
                }}
                className="peer sr-only"
                data-testid="admin-parser-schedule-enabled"
              />
              <span className="w-10 h-5 rounded-full bg-slate-300 peer-checked:bg-emerald-500 transition-colors block" />
              <span className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5" />
            </span>
            <span className="text-sm font-semibold text-slate-700">
              {cfg.schedule_enabled ? 'Enabled' : 'Disabled'}
            </span>
          </label>

          {/* Frequency dropdown */}
          <label className="inline-flex items-center gap-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Run every</span>
            <select
              value={Number(cfg.schedule_interval_minutes) || 1440}
              disabled={!cfg.schedule_enabled}
              onChange={(e) => {
                const v = Number(e.target.value);
                setCfg({ ...cfg, schedule_interval_minutes: v });
                saveSchedule({ schedule_interval_minutes: v });
              }}
              className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm font-mono bg-white disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="admin-parser-schedule-interval"
            >
              <option value={60}>Every 1 hour</option>
              <option value={180}>Every 3 hours</option>
              <option value={360}>Every 6 hours</option>
              <option value={720}>Every 12 hours</option>
              <option value={1440}>Every 24 hours (daily)</option>
              <option value={10080}>Every 7 days (weekly)</option>
            </select>
          </label>

          {/* Next-run hint */}
          {cfg.schedule_enabled && (
            <span className="text-xs text-slate-500" data-testid="admin-parser-schedule-next">
              {nextRunLabel(cfg)}
            </span>
          )}
        </div>
      </section>

      {/* ── Sync actions ─────────────────────────────────── */}
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 sm:p-5">
        <h3 className="font-semibold text-slate-800 mb-3">Sync actions</h3>

        {/* Diagnostic — fetch + parse + report, WITHOUT touching the DB.
            Use this whenever a sync silently reports "inserted 0" —
            the panel below breaks down exactly where things fall off
            (auth, empty download, header rename, brand/model missing). */}
        <div className="mb-3 flex flex-wrap gap-2 items-center">
          <button
            onClick={() => runTest('full')} disabled={busy || testing}
            className="px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-300 disabled:opacity-50"
            data-testid="admin-parser-test"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertCircle className="w-4 h-4" />}
            <span>{testing ? 'Testing…' : 'Test parser (no DB write)'}</span>
          </button>
          <span className="text-xs text-slate-500">
            Run this first when sync counters look wrong — it diagnoses without touching the catalog.
          </span>
        </div>

        {testReport && (
          <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs" data-testid="admin-parser-test-report">
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 font-mono">
              <div><b>URL:</b> <span className="break-all">{testReport.url}</span></div>
              <div><b>HTTP:</b> <span className={testReport.http_status === 200 ? 'text-emerald-700' : 'text-red-700 font-bold'}>{testReport.http_status ?? '—'}</span></div>
              <div><b>Downloaded:</b> {testReport.downloaded_bytes?.toLocaleString('en-US')} bytes</div>
              <div><b>Raw rows in xlsx:</b> {testReport.total_rows_in_file?.toLocaleString('en-US')}</div>
              <div className="sm:col-span-2">
                <b>Rows that would be imported:</b>{' '}
                <span className={testReport.rows_with_brand_model > 0 ? 'text-emerald-700' : 'text-red-700 font-bold'}>
                  {testReport.rows_with_brand_model?.toLocaleString('en-US')}
                </span>
              </div>
            </div>
            {testReport.error && (
              <div className="mt-2 p-2 bg-red-100 border border-red-300 rounded text-red-800">
                <b>Error:</b> {testReport.error}
              </div>
            )}
            {testReport.headers_found?.length > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer font-semibold text-slate-700">
                  Headers found in xlsx ({testReport.headers_found.length})
                </summary>
                <div className="mt-1 ml-4 text-slate-600">
                  {testReport.headers_found.map((h, i) => {
                    const mapped = testReport.columns_mapped?.[h];
                    return (
                      <div key={i}>
                        <code>{h}</code>{' '}
                        {mapped
                          ? <span className="text-emerald-700">→ {mapped}</span>
                          : <span className="text-red-700">→ IGNORED (add to _PARSER_HEADER_MAP)</span>}
                      </div>
                    );
                  })}
                </div>
              </details>
            )}
            {testReport.sample_row && (
              <details className="mt-2">
                <summary className="cursor-pointer font-semibold text-slate-700">Sample row that WOULD be imported</summary>
                <pre className="mt-1 ml-4 text-slate-600 overflow-x-auto">{JSON.stringify(testReport.sample_row, null, 2)}</pre>
              </details>
            )}
            {testReport.sample_dropped && (
              <details className="mt-2" open>
                <summary className="cursor-pointer font-semibold text-amber-800">
                  ⚠ Sample row that was DROPPED (missing: {(testReport.sample_dropped.missing || []).join(', ') || '—'})
                </summary>
                <pre className="mt-1 ml-4 text-slate-600 overflow-x-auto">{JSON.stringify(testReport.sample_dropped, null, 2)}</pre>
              </details>
            )}
          </div>
        )}

        <div className="grid sm:grid-cols-3 gap-3">
          {/* Delta — fastest path, only rows the parser hasn't yet flagged delivered */}
          <button
            onClick={() => runSync('delta')} disabled={busy}
            className="w-full px-4 py-3 rounded-lg font-bold text-sm flex items-center justify-center gap-2 bg-gradient-to-r from-violet-500 to-violet-600 hover:from-violet-600 hover:to-violet-700 text-white shadow-sm disabled:opacity-50 transition"
            data-testid="admin-parser-sync-delta"
          >
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span>{syncing ? 'Syncing…' : 'Sync delta'}</span>
          </button>
          {/* Full — re-imports the whole catalog (idempotent) */}
          <button
            onClick={() => runSync('full')} disabled={busy}
            className="w-full px-4 py-3 rounded-lg font-bold text-sm flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white shadow-sm disabled:opacity-50 transition"
            data-testid="admin-parser-sync-full"
          >
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span>{syncing ? 'Syncing…' : 'Sync full'}</span>
          </button>
          {/* Manual upload — admin drops a fresh xlsx */}
          <label
            className={`w-full px-4 py-3 rounded-lg font-bold text-sm flex items-center justify-center gap-2 cursor-pointer bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white shadow-sm transition ${busy ? 'opacity-50 pointer-events-none' : ''}`}
            data-testid="admin-parser-upload-label"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            <span>{uploading ? 'Uploading…' : 'Upload .xlsx file'}</span>
            <input
              ref={fileInputRef}
              type="file" accept=".xlsx"
              onChange={runUpload}
              className="hidden"
              disabled={busy}
              data-testid="admin-parser-upload"
            />
          </label>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          <b>Delta</b> pulls only rows the parser hasn&apos;t yet flagged as delivered (fast).
          <b className="ml-2">Full</b> re-imports the entire catalog (idempotent — existing customer purchases stay linked).
          <b className="ml-2">Upload</b> takes any local .xlsx with the same column layout.
        </p>
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2 mt-2" data-testid="admin-parser-delta-hint">
          If <b>Total files</b> above is lower than the parser&apos;s own &quot;files in DB&quot; count, use <b>Sync full</b>.
          Delta can never bring back rows the parser has already marked as exported, so a gap stays a gap until a full sync runs.
        </p>
      </section>

      {/* ── Last-sync banner ─────────────────────────────── */}
      {lastAt ? (
        <div className={`rounded-lg p-3 text-xs ${lastErr ? 'bg-rose-50 border border-rose-200' : 'bg-emerald-50 border border-emerald-200'}`} data-testid="admin-parser-banner">
          {lastErr ? (
            <>
              <AlertCircle className="inline w-4 h-4 mr-1 text-rose-600" />
              <span className="font-semibold text-rose-700">Last sync failed</span>
              <span className="text-slate-600 ml-1">at {new Date(lastAt).toLocaleString()} — {lastErr}</span>
            </>
          ) : (
            <>
              <CheckCircle2 className="inline w-4 h-4 mr-1 text-emerald-600" />
              <span className="font-semibold text-emerald-700">Last sync ok</span>
              <span className="text-slate-600 ml-1">
                at {new Date(lastAt).toLocaleString()} —
                inserted {cfg.last_sync_inserted ?? 0},
                updated {cfg.last_sync_updated ?? 0},
                skipped {cfg.last_sync_skipped ?? 0}
                {cfg.last_sync_total_rows ? ` (total ${cfg.last_sync_total_rows} rows` : ''}
                {cfg.last_sync_elapsed_sec ? `, ${Math.round(cfg.last_sync_elapsed_sec)}s)` : (cfg.last_sync_total_rows ? ')' : '')}
                {cfg.last_sync_source === 'manual_xlsx' ? ' · via manual upload' : ''}
              </span>
            </>
          )}
        </div>
      ) : (
        <div className="rounded-lg p-3 text-xs bg-slate-50 border border-slate-200 text-slate-600">
          Sync has not been run yet on this server.
        </div>
      )}

      {(cfg.last_sync_warnings?.length || 0) > 0 && (
        <div className="rounded-lg p-3 text-xs bg-amber-50 border border-amber-200" data-testid="admin-parser-warnings">
          <p className="font-semibold text-amber-700 mb-1">Parser warnings:</p>
          <ul className="list-disc list-inside text-slate-700">
            {cfg.last_sync_warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function nextRunLabel(cfg) {
  // Compute the next scheduled fire time given last_sync_at + interval.
  // Falls back to "soon" when we don't have a previous run yet.
  const intervalMin = Number(cfg.schedule_interval_minutes) || 0;
  if (!intervalMin) return '';
  const last = cfg.last_sync_at ? new Date(cfg.last_sync_at) : null;
  if (!last) return 'Next run: within a minute';
  const next = new Date(last.getTime() + intervalMin * 60 * 1000);
  if (next.getTime() <= Date.now()) return 'Next run: within a minute';
  const mins = Math.round((next.getTime() - Date.now()) / 60000);
  if (mins < 60)   return `Next run: in ~${mins} min`;
  if (mins < 1440) return `Next run: in ~${Math.round(mins / 60)} h`;
  return `Next run: ${next.toLocaleString()}`;
}

function StatTile({ icon: Icon, color, label, value }) {
  const colorMap = {
    cyan:    { bg: 'bg-cyan-50',    text: 'text-cyan-700',    border: 'border-cyan-100' },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-100' },
    violet:  { bg: 'bg-violet-50',  text: 'text-violet-700',  border: 'border-violet-100' },
    amber:   { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-100' },
  };
  const c = colorMap[color] || colorMap.cyan;
  const display = value === null || value === undefined
    ? '—'
    : Number(value).toLocaleString();
  return (
    <div className={`rounded-xl border ${c.border} ${c.bg} p-4 flex items-center gap-3`} data-testid={`admin-parser-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className={`w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center`}>
        <Icon className={`w-5 h-5 ${c.text}`} />
      </div>
      <div className="min-w-0">
        <p className={`text-xs uppercase font-bold tracking-wider ${c.text}`}>{label}</p>
        <p className="text-xl font-extrabold text-slate-900 tabular-nums">{display}</p>
      </div>
    </div>
  );
}
